import React from 'react'
import { Alert, AlertTitle, Button } from '@mui/material'
import { Science as ScienceIcon } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import {
  calculateStaleness,
  formatRelativeTime,
  buildReminderMessage,
  STALENESS_LEVEL,
  STALENESS_SEVERITY
} from '../../utils/stalenessUtils'

/**
 * TestReminder - A color-coded banner that reminds users when their last
 * pool measurement is getting stale. Displays relative time, an urgency
 * message, and a "Test Now" button linking to the measurement page.
 *
 * Does not render if:
 * - No measuredAt date is provided (no measurements exist)
 * - The measurement is less than 24 hours old (FRESH)
 * - The banner has been dismissed
 *
 * @param {Object} props
 * @param {string|Date|null} props.measuredAt - Date of the latest measurement
 * @param {boolean} props.dismissed - Whether the user has dismissed the banner this session
 * @param {Function} props.onDismiss - Callback when the user closes the banner
 */
const TestReminder = ({ measuredAt, dismissed, onDismiss }) => {
  // Do not show if no measurement data exists
  if (!measuredAt) {
    return null
  }

  // Do not show if dismissed
  if (dismissed) {
    return null
  }

  const staleness = calculateStaleness(measuredAt)

  // Do not show if measurement is fresh (< 24 hours)
  if (staleness === STALENESS_LEVEL.FRESH) {
    return null
  }

  const relativeTime = formatRelativeTime(measuredAt)
  const message = buildReminderMessage(relativeTime, staleness)
  const severity = STALENESS_SEVERITY[staleness]

  return (
    <Alert
      severity={severity}
      onClose={onDismiss}
      action={
        <Button
          component={Link}
          to="/add-measurement"
          color="inherit"
          size="small"
          startIcon={<ScienceIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}
        >
          Test Now
        </Button>
      }
      sx={{
        mb: 2,
        alignItems: 'center',
        '& .MuiAlert-action': {
          pt: 0,
          alignItems: 'center'
        }
      }}
    >
      <AlertTitle sx={{ mb: 0, fontWeight: 600 }}>
        {message}
      </AlertTitle>
    </Alert>
  )
}

export default TestReminder
